import type { EntityViewType } from "@config";
import { useIsPdf } from "@library/components";
import { useIsMobile } from "@library/shared";
import classNames from "classnames";
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import {
  TimelineCtxProvider,
  TimelineScrollAnimCtxProvider,
  type IMarkerSize,
} from "../ctx";
import { useTimelineItemsInView } from "../hooks";
import "../styles/timeline.scss";
import {
  TimelineContentWrappingEnum,
  TimelinePositionEnum,
  TimelineTypeEnum,
  type ITimelineEditorData,
} from "../types";
import {
  ensureTimelineComboSettings,
  makeTimelineComboAttributes,
} from "../utils";
import { TimelineHorizontalButtons } from "./timeline-horizontal-buttons";
import { TimelineHorizontalMobile } from "./timeline-horizontal-mobile";
import { TimelineHorizontalNewLine } from "./timeline-horizontal-newline";
import { TimelineItemsWrapper } from "./timeline-items-wrapper";

interface IProps {
  entity: EntityViewType;
  children: ReactNode;
  editorData?: ITimelineEditorData;
}

export const TimelineView = ({ entity, children, editorData }: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useIsMobile();
  const isPdf = useIsPdf();

  const comboSettings = useMemo(() => {
    return ensureTimelineComboSettings(entity.settings, isMobile, isPdf);
  }, [entity.settings, isMobile, isPdf]);

  const [movedBy, setMovedBy] = useState(0);
  const amtOfSets = entity.children.length;
  const itemsInView = useTimelineItemsInView({ entity, isMobile });

  const [markerSizes, setMarkerSizes] = useState<Map<number, IMarkerSize>>(
    () => new Map(),
  );
  const registerMarkerSize = useCallback((idx: number, size: IMarkerSize) => {
    setMarkerSizes((prev) => {
      const existing = prev.get(idx);
      // Must compare xOffsetInCell too: a preset switch can keep the
      // marker's size but move it within the cell (e.g. centered →
      // left). Without this the snake-line mask cuts stay anchored to
      // the old position.
      if (
        existing &&
        existing.width === size.width &&
        existing.height === size.height &&
        existing.xOffsetInCell === size.xOffsetInCell
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(idx, size);
      return next;
    });
  }, []);

  const [pos0Heights, setPos0Heights] = useState<Map<number, number>>(
    () => new Map(),
  );
  const registerPos0Height = useCallback((idx: number, height: number) => {
    setPos0Heights((prev) => {
      if (prev.get(idx) === height) return prev;
      const next = new Map(prev);
      next.set(idx, height);
      return next;
    });
  }, []);

  const [pos1Heights, setPos1Heights] = useState<Map<number, number>>(
    () => new Map(),
  );
  const registerPos1Height = useCallback((idx: number, height: number) => {
    setPos1Heights((prev) => {
      if (prev.get(idx) === height) return prev;
      const next = new Map(prev);
      next.set(idx, height);
      return next;
    });
  }, []);

  const isVertical =
    (comboSettings.timelineType ?? TimelineTypeEnum.Vertical) ===
    TimelineTypeEnum.Vertical;
  const isHorizontal = !isVertical;
  const isTimelineOnLeft =
    comboSettings.timelinePosition === TimelinePositionEnum.Left;

  let itemsMovedBy = 0;
  if (!isMobile) {
    itemsMovedBy = Math.max(0, Math.min(amtOfSets - itemsInView, movedBy));
  } else itemsMovedBy = 0;

  return (
    <TimelineCtxProvider
      ctxState={{
        isPdf,
        comboId: entity.id,
        isMobile,
        isVertical,
        isHorizontal,
        totalAmtOfItems: amtOfSets,
        itemsInView,
        itemsMovedBy,
        setMovedBy: setMovedBy,
        comboSettings,
        markerSizes,
        registerMarkerSize,
        pos0Heights,
        registerPos0Height,
        pos1Heights,
        registerPos1Height,
      }}
    >
      <div
        {...makeTimelineComboAttributes(comboSettings)}
        ref={containerRef}
        style={{
          // @ts-ignore
          "--timeline-color": comboSettings.timelineColor,
          "--timeline-marker-text-color":
            comboSettings.timelineMarkerTextColor ?? "#FFFFFF",
          "--timeline-items-gap": "32px",
        }}
        className={classNames({
          "combo timeline-combo tw-relative tw-flex": true,
          // Vertical
          "tw-flex-col": isVertical,
          "tw-p-4": !isMobile && isVertical,
          "tw-py-4": isMobile && isVertical,
          "tw-pl-4 tw-pr-2": isMobile && isVertical && !isTimelineOnLeft,
          "tw-pl-2 tw-pr-4": isMobile && isVertical && isTimelineOnLeft,
          // Horizontal - carousel.
          "tw-flex-row tw-flex-nowrap tw-items-start !tw-justify-start tw-px-0 tw-py-4 [overflow-x:clip] [overflow-y:visible]":
            isHorizontal &&
            comboSettings.timelineContentWrapping !==
              TimelineContentWrappingEnum.NewLine,
          // Horizontal - new line (wrapping)
          "tw-flex-col tw-px-0 tw-py-4":
            isHorizontal &&
            comboSettings.timelineContentWrapping ===
              TimelineContentWrappingEnum.NewLine,
        })}
      >
        {isHorizontal ? (
          <>
            {comboSettings.timelineContentWrapping ===
            TimelineContentWrappingEnum.NewLine ? (
              <TimelineHorizontalNewLine>{children}</TimelineHorizontalNewLine>
            ) : (
              <>
                {!isMobile && <TimelineHorizontalButtons />}
                {isMobile ? (
                  <TimelineHorizontalMobile>
                    {children}
                  </TimelineHorizontalMobile>
                ) : (
                  <TimelineItemsWrapper>{children}</TimelineItemsWrapper>
                )}
              </>
            )}
          </>
        ) : (
          <TimelineScrollAnimCtxProvider
            containerRef={containerRef}
            editorData={editorData}
          >
            <TimelineItemsWrapper>{children}</TimelineItemsWrapper>
          </TimelineScrollAnimCtxProvider>
        )}
      </div>
    </TimelineCtxProvider>
  );
};
